import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, MenuItem, Select, FormControl, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { PageTitle } from "@/components/page.title";
import { StyledButton } from "@/components/button";
import { AppRoutes } from "@/constants/app.routes";
import { AppConfig } from "@/config/app.config";

import { ContentWrapper } from "./styles";

type BatchKey = string;

export const BatchSelect = () => {
  const navigate = useNavigate();
  const [selectedBatch, setSelectedBatch] = useState<BatchKey>(AppConfig.PRESELECTED_BATCH);
  const [loading, setLoading] = useState(true);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  useEffect(() => {
    const initBatch = () => {
      // If batch selection is disabled, auto-select default and navigate
      if (!AppConfig.SHOW_BATCH_SELECTION) {
        localStorage.setItem("selectedBatch", AppConfig.DEFAULT_BATCH);
        navigate(AppRoutes.select_difficulty);
        return;
      }

      // Check if already selected
      const stored = localStorage.getItem("selectedBatch");
      if (stored && AppConfig.BATCHES[stored]) {
        setSelectedBatch(stored);
      }

      setLoading(false);
    };

    initBatch();
  }, [navigate]);

  const handleBatchChange = (event: any) => {
    const newBatch = event.target.value as BatchKey;
    setSelectedBatch(newBatch);
    localStorage.setItem("selectedBatch", newBatch);
  };

  const handleNavigate = () => {
    navigate(AppRoutes.select_difficulty);
  };

  // Get unique display names with their corresponding batch keys
  const uniqueBatches = React.useMemo(() => {
    const seen = new Map<string, string>(); // displayName -> batchKey
    Object.entries(AppConfig.BATCHES).forEach(([key, config]) => {
      if (!seen.has(config.displayName)) {
        seen.set(config.displayName, key);
      }
    });
    return Array.from(seen.entries()).map(([displayName, key]) => ({
      key,
      displayName,
    }));
  }, []);

  if (loading) {
    return (
      <ContentWrapper>
        <PageTitle>Lädt...</PageTitle>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PageTitle>WÄHLE DEINE AUFLAGE</PageTitle>

      <Box
        sx={(theme) => ({
          margin: theme.spacing(8, 0),
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
          alignItems: "center",
        })}
      >
        <FormControl
          sx={(theme) => ({
            width: "80%",
            maxWidth: "32rem",
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.paper,
              fontSize: "1.6rem",
            },
          })}
        >
          <Select
            value={selectedBatch}
            onChange={handleBatchChange}
            displayEmpty
          >
            {uniqueBatches.map(({ key, displayName }) => (
              <MenuItem key={key} value={key}>
                {displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <IconButton
          onClick={() => setInfoDialogOpen(true)}
          sx={(theme) => ({
            color: theme.palette.primary.main,
            padding: theme.spacing(1),
          })}
          aria-label="info"
        >
          <InfoOutlinedIcon sx={{ fontSize: "2.4rem" }} />
        </IconButton>
      </Box>

      <Box textAlign="center">
        <StyledButton
          variant="contained"
          onClick={handleNavigate}
        >
          WEITER
        </StyledButton>
      </Box>

      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "2rem", fontWeight: "bold" }}>
          Hinweis zur Auflage
        </DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: "1.6rem", lineHeight: 1.6 }}>
             Öffne jetzt die Verpackung. Wenn unter der Aufreißlasche 'Auflage 2' steht, wähle diese aus. Wenn dort nichts steht, wähle Auflage 1.
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <StyledButton
            variant="contained"
            onClick={() => setInfoDialogOpen(false)}
          >
            VERSTANDEN
          </StyledButton>
        </DialogActions>
      </Dialog>
    </ContentWrapper>
  );
};

